import React, { useState } from 'react'
import { getLeadsByStatusDate, DownloadLeadsByStatusDate } from '../../MarketingDashboardNew/ActionCreator';
import moment from 'moment';
import Pagination from '@mui/material/Pagination';

const ROWS_PER_PAGE = 8;

// ── Popup Component ──────────────────────────────────────────────────────────
function PopupTable({ data, onClose, downloadParams }) {
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadCSV = () => {
    if (!downloadParams) return;
    setDownloading(true);
    const { start, to, statusName } = downloadParams;
    DownloadLeadsByStatusDate(start, to, statusName, () => {
      setDownloading(false);
    });
  };

  // Reset to page 1 whenever a new popup opens
  React.useEffect(() => {
    setPage(1);
  }, [data?.rows]);

  if (!data) return null;

  const totalRows = data.rows?.length ?? 0;
  const totalPages = Math.ceil(totalRows / ROWS_PER_PAGE);
  const paginatedRows = data.rows?.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '12px',
          width: '760px',
          maxWidth: '95vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}
      >
        {/* ── Spin keyframe for download button loader ─────────────────── */}
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0,
          }}
        >

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              {data.title || 'Details'}
            </h3>
            {/* Record count badge */}
            {totalRows > 0 && !data.loading && (
              <span
                style={{
                  background: '#EEF2FF',
                  color: '#4F46E5',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '20px',
                  padding: '2px 10px',
                }}
              >
                {totalRows} records
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Download Button */}
            {!data?.loading && !data?.error && (data?.rows?.length ?? 0) > 0 && (
              <button
                onClick={handleDownloadCSV}
                disabled={downloading}
                title="Download CSV"
                style={{
                  border: 'none',
                  background: downloading ? '#e5e7eb' : '#4F46E5',
                  borderRadius: '6px',
                  height: '32px',
                  padding: '0 12px',
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  color: downloading ? '#9ca3af' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {downloading ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                    Downloading…
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                    Download
                  </>
                )}
              </button>
            )}

            <button
              onClick={onClose}
              style={{
                border: 'none',
                background: '#f3f4f6',
                borderRadius: '6px',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* ── Table (scrollable area) ──────────────────────────────────────── */}
        <div style={{ overflowY: 'auto', padding: '0 20px', flex: 1 }}>
          {data.loading ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '48px 0' }}>
              Loading…
            </p>
          ) : data.error ? (
            <p style={{ textAlign: 'center', color: '#ef4444', padding: '48px 0' }}>
              {data.error}
            </p>
          ) : totalRows === 0 ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '48px 0' }}>
              No records found.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#f9fafb' }}>
                  {/* Serial no. column */}
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      color: '#8AA09A',
                      fontWeight: 600,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #e5e7eb',
                      letterSpacing: '0.05em',
                      width: '48px',
                    }}
                  >
                    #
                  </th>
                  {data.columns?.map((col) => (
                    <th
                      key={col}
                      style={{
                        textAlign: 'left',
                        padding: '10px 12px',
                        color: '#8AA09A',
                        fontWeight: 600,
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        borderBottom: '1px solid #e5e7eb',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows?.map((row, i) => {
                  const globalIndex = (page - 1) * ROWS_PER_PAGE + i + 1;
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        background: i % 2 === 0 ? '#fff' : '#fafafa',
                      }}
                    >
                      <td style={{ padding: '10px 12px', color: '#9ca3af', fontSize: '12px' }}>
                        {globalIndex}
                      </td>
                      {data.columns?.map((col) => (
                        <td
                          key={col}
                          style={{ padding: '10px 12px', color: '#374151' }}
                        >
                          {row[col] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination Footer ────────────────────────────────────────────── */}
        {!data.loading && !data.error && totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 20px',
              borderTop: '1px solid #e5e7eb',
              flexShrink: 0,
              background: '#fff',
            }}
          >
            {/* "Showing X – Y of Z" label */}
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Showing{' '}
              <strong style={{ color: '#374151' }}>
                {(page - 1) * ROWS_PER_PAGE + 1}–{Math.min(page * ROWS_PER_PAGE, totalRows)}
              </strong>{' '}
              of <strong style={{ color: '#374151' }}>{totalRows}</strong>
            </span>

            {/* MUI Pagination */}
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              size="small"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '13px',
                  color: '#374151',
                },
                '& .Mui-selected': {
                  backgroundColor: '#4F46E5 !important',
                  color: '#fff !important',
                  fontWeight: 600,
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: '#EEF2FF',
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function CustomStepper({ Data, hoverShow = false, dateFilterPopup }) {
  const [showHovered, setShowHovered] = useState(null);
  const [popup, setPopup] = useState(null);

  const fetchPopupData = (val) => {
    console.log("val", val);

    setPopup({
      title: val?.name || 'Details',
      loading: true,
      error: null,
      columns: [],
      rows: [],
    });

    const start = moment(dateFilterPopup?.from).format("DD-MM-YYYY");
    const to = moment(dateFilterPopup?.to).format("DD-MM-YYYY");

    getLeadsByStatusDate(start, to, val?.name == "Complete" ? "Thanks" : val?.name, (response) => {
      try {
        const leads = Array.isArray(response) ? response : (response?.data ?? []);

        if (!leads.length) {
          setPopup((prev) => ({ ...prev, loading: false, rows: [], columns: [] }));
          return;
        }

        const keys = Object.keys(leads[0]);
        const toLabel = (k) =>
          k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());

        const columns = keys.map(toLabel);
        const rows = leads.map((lead) => {
          const row = {};
          keys.forEach((k) => { row[toLabel(k)] = lead[k] ?? '—'; });
          return row;
        });

        setPopup({
          title: val?.status || val?.name || 'Details',
          loading: false,
          error: null,
          columns,
          rows,
        });
      } catch (err) {
        setPopup((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to parse response.',
        }));
      }
    });
  };

  const closePopup = () => setPopup(null);

  return (
    <>
      <div className='StepperOption' style={{ marginTop: '30px' }}>
        {Data?.length > 0
          ? Data.map((val, id) => (
            <React.Fragment key={id}>
              <div className='main-single-content-new'>
                <div
                  className={`boxed ${showHovered === id ? 'hovered' : ''}`}
                  onClick={() => fetchPopupData(val)}
                  // onMouseEnter={() => { if (hoverShow) setShowHovered(id); }}
                  // onMouseLeave={() => { if (hoverShow) setShowHovered(null); }}
                  style={{ cursor: 'pointer' }}
                >
                  <h6>{val?.value}</h6>
                </div>

                <h5>
                  {val?.name?.split(' ').map((word, index) => (
                    <React.Fragment key={index}>
                      {word}
                      {index === 0 && <br />}
                      {index !== 0 && ' '}
                    </React.Fragment>
                  ))}
                </h5>

                {showHovered === id && Object.keys(val?.hoverData ?? {}).length > 0 && (
                  <div
                    className={`mouseHoverSection ${id === 0 ? 'first' : id + 1 === Data.length ? 'last' : ''}`}
                    style={{ bottom: '-60%' }}
                  >
                    {Object.entries(val.hoverData).map(([key, pair], idx) => (
                      <div
                        key={idx}
                        style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}
                      >
                        <h6>{key}</h6>
                        <h6>{pair}</h6>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          ))
          : null}
      </div>

      <PopupTable data={popup} onClose={closePopup} downloadParams={popup ? {
        start: moment(dateFilterPopup?.from).format("DD-MM-YYYY"),
        to: moment(dateFilterPopup?.to).format("DD-MM-YYYY"),
        statusName: popup?.title === 'Complete' ? 'Thanks' : popup?.title,
      } : null} />
    </>
  );
}